/**
 * Splats casting into spot / point shadow maps with WebGPU's GPU-sorted splat renderer.
 *
 * The engine's GSplatShadowRenderer (one per splat manager) draws the splats into directional
 * maps only: per directional light it keeps an entry (a compute cull to an index list, an
 * indirect draw) whose mesh instance only that light's shadow camera sees. This patch gives it
 * entries for the spot and point lights the shadow system names:
 * - syncLights lets the engine's own syncLights keep the directional entries (it sees only those)
 *   and adds / removes the local ones;
 * - a local entry's cull keeps the splats inside a cube around the light, one range on each
 *   side (one list serves all six faces of a point light), and only runs in frames where the
 *   light's map is rendered (it is redrawn only on change); the entry stays visible in between,
 *   because the engine picks shadow casters before the cull runs. The engine's frame cull reads a
 *   light through getRenderData(sceneCamera, 0), a directional light's render data: for a local
 *   light it gets a stand-in with the range cube and the light's own cull request (see r.cull);
 * - a local entry's mesh instance is visible to every face camera of its light.
 * The splat shader writes a point light's distance / range depth itself (rl_casterCenter in
 * gsplat-mirror-clip.ts). WebGL2 and the CPU-sorted renderer draw the splats through the quad
 * renderer's own mesh instance instead (ShadowSystem.patchSplatCasterVisibility).
 *
 * A renderer whose internals don't look as expected is left alone (the scan then just doesn't
 * cast into spot / point maps on it).
 */
import * as pc from 'playcanvas';

interface ShadowEntry {
    light: pc.Light;
    meshInstance: pc.MeshInstance;
}

interface GsplatShadowRendererInternals {
    entries: Map<pc.Light, ShadowEntry>;
    cull: (...args: unknown[]) => void;
    syncLights: () => void;
    _createEntry: (light: pc.Light) => ShadowEntry;
    _destroyEntry: (entry: ShadowEntry) => void;
    __storysplatLocalCasters?: () => ReadonlySet<pc.Light>;
}

interface LightInternals {
    _type: number;
    numShadowFaces: number;
    attenuationEnd: number;
    enabled: boolean;
    castShadows: boolean;
    _node: pc.GraphNode;
    /** shadowCullRequested (PlayCanvas 2.23): the engine culls, so renders, this map this frame. */
    getRenderData(camera: pc.Camera | null, face: number): { shadowCamera: pc.Camera; shadowCullRequested?: boolean };
}

interface GsplatDirectorInternals {
    camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: { shadowRenderer?: GsplatShadowRendererInternals | null } }> }>;
}

function looksPatchable(r: Partial<GsplatShadowRendererInternals> | null | undefined): r is GsplatShadowRendererInternals {
    return !!r && r.entries instanceof Map && typeof r.syncLights === 'function'
        && typeof r._createEntry === 'function' && typeof r._destroyEntry === 'function' && typeof r.cull === 'function';
}

/** Every GSplatShadowRenderer the app's splat managers currently have. */
export function findGsplatShadowRenderers(app: pc.AppBase): GsplatShadowRendererInternals[] {
    const out: GsplatShadowRendererInternals[] = [];
    const director = (app.renderer as unknown as { gsplatDirector?: GsplatDirectorInternals } | undefined)?.gsplatDirector;
    director?.camerasMap?.forEach((cd) => cd.layersMap?.forEach((ld) => {
        const r = ld?.gsplatManager?.shadowRenderer;
        if (looksPatchable(r)) out.push(r);
    }));
    return out;
}

/**
 * True when `camera` is one of the light's face cameras (1 for a spot light, 6 for a point light)
 * as the light has them now. Ask at cull time, never keep them from an earlier point in the frame:
 * the engine replaces a light's face cameras whenever it drops its render data (a new shadow type,
 * map size or cast-shadows flag; see lightShadowType in shadowSystem.ts), and a test against the
 * old ones keeps the splats out of the map rendered with the new ones.
 */
export function isLightFaceCamera(light: pc.Light, camera: pc.Camera): boolean {
    const l = light as unknown as LightInternals;
    for (let face = 0; face < l.numShadowFaces; face++) {
        if (l.getRenderData(null, face).shadowCamera === camera) return true;
    }
    return false;
}

/**
 * A local entry the engine hid (its cull found nothing ready) stays visible to the next frame's
 * shadow-caster cull, which runs before its own cull, but draws nothing until a cull succeeds.
 */
function keepCandidate(entry: ShadowEntry): void {
    const mi = entry.meshInstance;
    if (mi.visible) return;
    mi.visible = true;
    mi.instancingCount = 0;
}

function isLocal(light: pc.Light): boolean {
    return (light as unknown as LightInternals)._type !== pc.LIGHTTYPE_DIRECTIONAL;
}

/** Six inward planes of the cube centred on the light, `range` to each side. */
function rangeCubePlanes(light: pc.Light): Array<{ normal: pc.Vec3; distance: number }> {
    const l = light as unknown as LightInternals;
    const p = l._node.getPosition();
    const r = l.attenuationEnd;
    return [
        { normal: new pc.Vec3(1, 0, 0), distance: -(p.x - r) },
        { normal: new pc.Vec3(-1, 0, 0), distance: p.x + r },
        { normal: new pc.Vec3(0, 1, 0), distance: -(p.y - r) },
        { normal: new pc.Vec3(0, -1, 0), distance: p.y + r },
        { normal: new pc.Vec3(0, 0, 1), distance: -(p.z - r) },
        { normal: new pc.Vec3(0, 0, -1), distance: p.z + r },
    ];
}

/**
 * What the engine's frame cull reads from a local light in place of getRenderData(sceneCamera, 0)
 * (a directional light's render data, never culled or rendered for a local light):
 * - shadowCamera: the range cube, its planes as objects (PlayCanvas 2.21) and packed in planeData
 *   (2.23), and no size threshold (orthoHeight 0);
 * - shadowCullRequested / shadowCascadeMask: 2.23 culls an entry only in frames whose shadow
 *   passes requested its light's cull. A local light's request is on its own render data (face 0):
 *   pass that on, so the cull runs exactly when the map is redrawn.
 */
function localCullView(light: pc.Light) {
    const planes = rangeCubePlanes(light);
    const planeData = new Float32Array(24);
    planes.forEach((plane, i) => planeData.set([plane.normal.x, plane.normal.y, plane.normal.z, plane.distance], i * 4));
    return {
        shadowCamera: { frustum: { planes, planeData }, orthoHeight: 0 },
        shadowCullRequested: !!(light as unknown as LightInternals).getRenderData(null, 0).shadowCullRequested,
        shadowCascadeMask: 1,
    };
}

/**
 * Patch every current GSplatShadowRenderer (once each) so it also draws the splats into the
 * maps of `localLights()`. Call it every frame while splats should cast into local lights:
 * renderers are created lazily. Returns how many renderers are patched.
 */
export function patchGsplatShadowRenderers(app: pc.AppBase, localLights: () => ReadonlySet<pc.Light>): number {
    const renderers = findGsplatShadowRenderers(app);
    for (const r of renderers) {
        r.__storysplatLocalCasters = localLights;
        if ((r as unknown as { __storysplatPatched?: boolean }).__storysplatPatched) continue;
        (r as unknown as { __storysplatPatched?: boolean }).__storysplatPatched = true;

        // The engine's own syncLights keeps the directional entries (where it finds the layer's
        // directional lights changed between engine versions) and destroys every other entry:
        // run it without the local ones, then add / remove those.
        const originalSync = r.syncLights;
        r.syncLights = function (this: GsplatShadowRendererInternals) {
            const local = new Map<pc.Light, ShadowEntry>();
            this.entries.forEach((entry, light) => {
                if (isLocal(light)) local.set(light, entry);
            });
            local.forEach((_entry, light) => this.entries.delete(light));
            originalSync.call(this);
            const desired = new Set<pc.Light>();
            for (const light of this.__storysplatLocalCasters?.() ?? []) {
                const l = light as unknown as LightInternals;
                if (l.enabled && l.castShadows && isLocal(light)) desired.add(light);
            }
            local.forEach((entry, light) => {
                if (desired.has(light)) this.entries.set(light, entry);
                else this._destroyEntry(entry);
            });
            desired.forEach((light) => {
                if (this.entries.has(light)) return;
                const entry = this._createEntry(light);
                entry.meshInstance.isVisibleFunc = (camera: pc.Camera) => isLightFaceCamera(light, camera);
                this.entries.set(light, entry);
            });
        };

        // The engine's frame cull reads each entry's light through getRenderData(sceneCamera, 0):
        // hand a local light's the stand-in for this call (localCullView). It culls a local entry
        // only in frames that redraw its map (it is redrawn only on change); in other frames the
        // entry is left visible: the engine picks a light's shadow casters BEFORE this runs
        // (cullShadowmaps, then gsplatDirector.updateShadows), so an instance hidden here would
        // miss the next frame that does redraw the map, and the splats would never reach a map
        // redrawn only once.
        const originalFrameCull = r.cull;
        r.cull = function (this: GsplatShadowRendererInternals, ...args: unknown[]) {
            const swapped: Array<{ light: LightInternals; own: boolean; previous: LightInternals['getRenderData'] }> = [];
            this.entries.forEach((_entry, light) => {
                if (!isLocal(light)) return;
                const l = light as unknown as LightInternals;
                const view = localCullView(light);
                swapped.push({ light: l, own: Object.prototype.hasOwnProperty.call(l, 'getRenderData'), previous: l.getRenderData });
                (l as { getRenderData: unknown }).getRenderData = () => view;
            });
            try {
                originalFrameCull.apply(this, args);
            } finally {
                for (const { light, own, previous } of swapped) {
                    if (own) (light as { getRenderData: unknown }).getRenderData = previous;
                    else delete (light as { getRenderData?: unknown }).getRenderData;
                }
            }
            // Not ready (no sorted splats yet): the engine hides every entry. Keep the local ones
            // candidates for the next redraw, drawing nothing now.
            this.entries.forEach((entry) => {
                if (isLocal(entry.light)) keepCandidate(entry);
            });
        };
    }
    return renderers.length;
}
